/**
 * Virtual Feed Hook
 *
 * Combines @tanstack/react-virtual with infinite scroll for high-performance
 * feeds. Only renders visible posts plus an overscan buffer.
 *
 * Based on ~/social/apps/ui/src/composables/useVirtualFeed.ts
 */

import { useRef, useCallback, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Post } from '@/types';

export interface UseVirtualFeedOptions {
  /** Flat array of all loaded posts */
  posts: Post[];
  /** Fetch next page callback */
  fetchNextPage: () => void;
  /** Whether more pages exist */
  hasNextPage: boolean;
  /** Whether currently fetching */
  isFetchingNextPage: boolean;
  /** Estimated item height or function */
  estimateSize?: number | ((index: number) => number);
  /** Items to render outside viewport (default: 5) */
  overscan?: number;
}

/**
 * Estimate post height based on content type.
 * Matches ~/social/apps/ui useVirtualFeed.ts estimatePostHeight().
 */
export function estimatePostHeight(post: Post): number {
  const postType = post.type || post.postType;

  if (postType === 'SONG') {
    let height = 160;
    if ((post.title ?? '').length > 40) height += 25;
    return height;
  }

  // Base height for header + engagement bar
  let height = 130;

  // Text content
  const text = post.content || (post as Record<string, unknown>).body as string | undefined;
  if (text) {
    const lines = Math.ceil(text.length / 60);
    height += Math.min(lines * 22, 200);
  }

  // Video posts get a large thumbnail area
  if (postType === 'VIDEO') {
    height += 460;
  }

  // Images
  if (post.images && Array.isArray(post.images) && post.images.length > 0) {
    height += 400;
  }

  return height;
}

export function useVirtualFeed({
  posts,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  estimateSize,
  overscan = 5,
}: UseVirtualFeedOptions) {
  const scrollElementRef = useRef<HTMLDivElement>(null);

  const resolvedEstimateSize = useCallback(
    (index: number) => {
      if (typeof estimateSize === 'function') return estimateSize(index);
      if (typeof estimateSize === 'number') return estimateSize;
      // Default: use post-type-aware estimation
      const post = posts[index];
      return post ? estimatePostHeight(post) : 400;
    },
    // We intentionally depend on posts.length to recalculate when posts change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [estimateSize, posts.length],
  );

  const virtualizer = useVirtualizer({
    count: posts.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: resolvedEstimateSize,
    overscan,
    // Measure actual DOM elements for accurate positioning (except Firefox)
    measureElement:
      typeof window !== 'undefined' && !navigator.userAgent.includes('Firefox')
        ? (element) => element.getBoundingClientRect().height
        : undefined,
  });

  // Infinite scroll: fetch more when near bottom
  useEffect(() => {
    const scrollEl = scrollElementRef.current;
    if (!scrollEl) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollEl;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceFromBottom < 500 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    // Check on mount in case content doesn't fill viewport
    handleScroll();

    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return {
    /** Attach this ref to the scrollable container div */
    scrollElementRef,
    /** Virtual items to render (only visible + overscan) */
    virtualItems,
    /** Total height for the inner content div */
    totalSize,
    /** Virtualizer instance for advanced control */
    virtualizer,
    /** Use as ref callback on each virtual item div for dynamic measurement */
    measureElement: virtualizer.measureElement,
    /** Scroll to a specific index */
    scrollToIndex: virtualizer.scrollToIndex.bind(virtualizer),
    /** Recalculate all item sizes */
    measure: virtualizer.measure.bind(virtualizer),
  };
}
